/* cSpell:words checkin hotellist ensen onsen HYOJI */
import { PageType, URL_KEYS } from 'promotions/constants/searchVacant';

import type { SearchVacantParams } from 'SearchVacant-Types';

/**
 * Parse array parameters from query
 */
const parseArray = (value: any): string[] | undefined => {
  if (!value) return undefined;
  if (Array.isArray(value)) return value;
  return [value];
};

/**
 * Parse number array parameters from query
 */
const parseNumberArray = (value: any): number[] | undefined => {
  if (!value) return undefined;
  const arr = Array.isArray(value) ? value : [value];
  const parsed = arr.map((v) => parseInt(v, 10)).filter((v) => !isNaN(v));
  return parsed.length > 0 ? parsed : undefined;
};

/**
 * Format date from year/month/day components
 */
const formatDate = (year: string, month: string, day: string): string | undefined => {
  if (!year || !month || !day) return undefined;
  const paddedMonth = month.padStart(2, '0');
  const paddedDay = day.padStart(2, '0');
  return `${year}-${paddedMonth}-${paddedDay}`;
};

/**
 * Parse integer from query value
 */
const parseInteger = (value: any): number | undefined => {
  if (!value) return undefined;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? undefined : parsed;
};

/**
 * Parse float from query value
 */
const parseFloat = (value: any): number | undefined => {
  if (!value) return undefined;
  const parsed = Number.parseFloat(value);
  return isNaN(parsed) ? undefined : parsed;
};

/**
 * Parse query parameters and convert to API request format
 * Only includes parameters that are present in the query
 */
export const parseSearchVacantQuery = (query: any) => {
  const params: any = {};

  // Page type and area (from URL query)
  if (query.page) params.page = query.page;
  if (query.area) params.area = query.area;

  // Date parameters
  const checkinDate = formatDate(
    query[URL_KEYS.CHECK_IN_YEAR],
    query[URL_KEYS.CHECK_IN_MONTH],
    query[URL_KEYS.CHECK_IN_DAY],
  );
  if (checkinDate) params.checkinDate = checkinDate;

  const checkoutDate = formatDate(
    query[URL_KEYS.CHECK_OUT_YEAR],
    query[URL_KEYS.CHECK_OUT_MONTH],
    query[URL_KEYS.CHECK_OUT_DAY],
  );
  if (checkoutDate) params.checkoutDate = checkoutDate;

  // Location parameters
  if (query[URL_KEYS.DAI]) params.pathId = query[URL_KEYS.DAI];
  const latitude = parseFloat(query.latitude);
  if (latitude !== undefined) params.latitude = latitude;
  const longitude = parseFloat(query.longitude);
  if (longitude !== undefined) params.longitude = longitude;
  const searchRadius = parseFloat(query.searchRadius);
  if (searchRadius !== undefined) params.searchRadius = searchRadius;

  // Guest parameters (required with defaults)
  params.roomNum = parseInteger(query[URL_KEYS.NO_OF_UNITS]) || 1;
  params.adultNum = parseInteger(query[URL_KEYS.ADULTS]) || 1;

  const upClassNum = parseInteger(query[URL_KEYS.CHILDREN_UPPER_GRADE]);
  if (upClassNum !== undefined) params.upClassNum = upClassNum;
  const lowClassNum = parseInteger(query[URL_KEYS.CHILDREN_LOWER_GRADE]);
  if (lowClassNum !== undefined) params.lowClassNum = lowClassNum;
  const infantWithMBNum = parseInteger(query[URL_KEYS.INFANT_BED_MEALS]);
  if (infantWithMBNum !== undefined) params.infantWithMBNum = infantWithMBNum;
  const infantWithMNum = parseInteger(query[URL_KEYS.INFANT_MEAL]);
  if (infantWithMNum !== undefined) params.infantWithMNum = infantWithMNum;
  const infantWithBNum = parseInteger(query[URL_KEYS.INFANT_BED]);
  if (infantWithBNum !== undefined) params.infantWithBNum = infantWithBNum;
  const infantWithoutMBNum = parseInteger(query[URL_KEYS.INFANT_NONE]);
  if (infantWithoutMBNum !== undefined) params.infantWithoutMBNum = infantWithoutMBNum;

  // Pagination parameters
  const offset = parseInteger(query[URL_KEYS.HYOJI]);
  if (offset !== undefined) params.offset = offset;
  const limit = parseInteger(query.limit);
  if (limit !== undefined) params.limit = limit;

  // Filter parameters
  const squeezeCondition = parseArray(query[URL_KEYS.SQUEEZES]);
  if (squeezeCondition) params.squeezeCondition = squeezeCondition;
  const roomCondition = parseArray(query.roomCondition);
  if (roomCondition) params.roomCondition = roomCondition;
  if (query.pointMin) params.pointMin = query.pointMin;
  if (query.roomEquip) params.roomEquip = query.roomEquip;
  const roomType = parseArray(query.roomType);
  if (roomType) params.roomType = roomType;
  const roomBedType = parseArray(query.roomBedType);
  if (roomBedType) params.roomBedType = roomBedType;
  const minCharge = parseInteger(query[URL_KEYS.KIN]);
  if (minCharge !== undefined) params.minCharge = minCharge;
  const maxCharge = parseInteger(query[URL_KEYS.KIN2]);
  if (maxCharge !== undefined) params.maxCharge = maxCharge;

  // Sorting and selection
  if (query[URL_KEYS.SORT]) params.sort = query[URL_KEYS.SORT];
  const providerIds = parseNumberArray(query[URL_KEYS.NUMBER]);
  if (providerIds) params.providerIds = providerIds;
  const seed = parseInteger(query.seed);
  if (seed !== undefined) {
    params.seed = seed;
  } else {
    // Generate random seed if not provided
    params.seed = Math.floor(Math.random() * 100000);
  }

  return params;
};

/**
 * Build API endpoint path based on page type from URL query
 * - vacant: /api/domestic-hotel-ad-search/datedAd/vacant/searchVacant (premium, onsen, pet)
 * - ski: /api/domestic-hotel-ad-search/datedAd/ski/searchVacant
 * - area: /api/domestic-hotel-ad-search/datedAd/hotellist/{area}/searchVacant
 * - midnight: /api/domestic-hotel-ad-search/datedAd/midnight/searchVacant
 * - air: /api/domestic-hotel-ad-search/datedAd/station/air/searchVacant
 * - ensen: /api/domestic-hotel-ad-search/datedAd/station/ensen/searchVacant
 */
export const buildEndpoint = (page?: string, area?: string): string => {
  const baseEndpoint = '/api/domestic-hotel-ad-search/datedAd';

  switch (page) {
    case PageType.VACANT:
      return `${baseEndpoint}/vacant/searchVacant`;
    case PageType.SKI:
      return `${baseEndpoint}/ski/searchVacant`;
    case PageType.AREA:
      if (!area) {
        throw new Error('Area is required when page is "area"');
      }
      return `${baseEndpoint}/hotellist/${area}/searchVacant`;
    case PageType.MIDNIGHT:
      return `${baseEndpoint}/midnight/searchVacant`;
    case PageType.AIR:
      return `${baseEndpoint}/station/air/searchVacant`;
    case PageType.ENSEN:
      return `${baseEndpoint}/station/ensen/searchVacant`;
    default:
      // Default to vacant search
      return `${baseEndpoint}/vacant/searchVacant`;
  }
};

/**
 * Build query string from parameters for API requests
 * Excludes seed as it is handled separately in request body
 */
export const buildQueryString = (params: SearchVacantParams): string => {
  const queryParams: Record<string, string> = {};

  // Page type and area (needed for URL routing)
  if (params.page) queryParams.page = params.page;
  if (params.area) queryParams.area = params.area;

  // Date parameters
  if (params.checkinDate) queryParams.checkinDate = params.checkinDate;
  if (params.checkoutDate) queryParams.checkoutDate = params.checkoutDate;

  // Location parameters
  if (params.pathId) queryParams.pathId = params.pathId;
  if (params.latitude !== null && params.latitude !== undefined)
    queryParams.latitude = String(params.latitude);
  if (params.longitude !== null && params.longitude !== undefined)
    queryParams.longitude = String(params.longitude);
  if (params.searchRadius !== null && params.searchRadius !== undefined)
    queryParams.searchRadius = String(params.searchRadius);

  // Guest parameters
  queryParams.roomNum = String(params.roomNum);
  queryParams.adultNum = String(params.adultNum);
  if (params.upClassNum !== null && params.upClassNum !== undefined)
    queryParams.upClassNum = String(params.upClassNum);
  if (params.lowClassNum !== null && params.lowClassNum !== undefined)
    queryParams.lowClassNum = String(params.lowClassNum);
  if (params.infantWithMBNum !== null && params.infantWithMBNum !== undefined)
    queryParams.infantWithMBNum = String(params.infantWithMBNum);
  if (params.infantWithMNum !== null && params.infantWithMNum !== undefined)
    queryParams.infantWithMNum = String(params.infantWithMNum);
  if (params.infantWithBNum !== null && params.infantWithBNum !== undefined)
    queryParams.infantWithBNum = String(params.infantWithBNum);
  if (params.infantWithoutMBNum !== null && params.infantWithoutMBNum !== undefined)
    queryParams.infantWithoutMBNum = String(params.infantWithoutMBNum);

  // Pagination parameters
  if (params.offset !== null && params.offset !== undefined)
    queryParams.offset = String(params.offset);
  if (params.limit !== null && params.limit !== undefined) queryParams.limit = String(params.limit);

  // Filter parameters
  if (params.squeezeCondition?.length)
    queryParams.squeezeCondition = params.squeezeCondition.join(',');
  if (params.roomCondition?.length) queryParams.roomCondition = params.roomCondition.join(',');
  if (params.pointMin) queryParams.pointMin = params.pointMin;
  if (params.roomEquip) queryParams.roomEquip = params.roomEquip;
  if (params.roomType?.length) queryParams.roomType = params.roomType.join(',');
  if (params.roomBedType?.length) queryParams.roomBedType = params.roomBedType.join(',');
  if (params.minCharge !== null && params.minCharge !== undefined)
    queryParams.minCharge = String(params.minCharge);
  if (params.maxCharge !== null && params.maxCharge !== undefined)
    queryParams.maxCharge = String(params.maxCharge);

  // Sorting and selection
  if (params.sort) queryParams.sort = params.sort;
  if (params.providerIds?.length) queryParams.providerIds = params.providerIds.join(',');

  return new URLSearchParams(queryParams).toString();
};

/**
 * Build URL query string from parameters using URL_KEYS format
 * This function converts API parameter names back to URL parameter names
 * Used for browser URL updates
 */
export const buildURLQueryString = (params: SearchVacantParams): string => {
  const queryParams = new URLSearchParams();

  // Page type and area
  if (params.page) queryParams.set('page', params.page);
  if (params.area) queryParams.set('area', params.area);

  // Date parameters - split back to year/month/day
  if (params.checkinDate) {
    const [year, month, day] = params.checkinDate.split('-');
    queryParams.set(URL_KEYS.CHECK_IN_YEAR, year);
    queryParams.set(URL_KEYS.CHECK_IN_MONTH, month);
    queryParams.set(URL_KEYS.CHECK_IN_DAY, day);
  }
  if (params.checkoutDate) {
    const [year, month, day] = params.checkoutDate.split('-');
    queryParams.set(URL_KEYS.CHECK_OUT_YEAR, year);
    queryParams.set(URL_KEYS.CHECK_OUT_MONTH, month);
    queryParams.set(URL_KEYS.CHECK_OUT_DAY, day);
  }

  // Location parameters
  if (params.pathId) queryParams.set(URL_KEYS.DAI, params.pathId);
  if (params.latitude !== null && params.latitude !== undefined)
    queryParams.set('latitude', String(params.latitude));
  if (params.longitude !== null && params.longitude !== undefined)
    queryParams.set('longitude', String(params.longitude));
  if (params.searchRadius !== null && params.searchRadius !== undefined)
    queryParams.set('searchRadius', String(params.searchRadius));

  // Guest parameters
  queryParams.set(URL_KEYS.NO_OF_UNITS, String(params.roomNum));
  queryParams.set(URL_KEYS.ADULTS, String(params.adultNum));
  if (params.upClassNum !== null && params.upClassNum !== undefined)
    queryParams.set(URL_KEYS.CHILDREN_UPPER_GRADE, String(params.upClassNum));
  if (params.lowClassNum !== null && params.lowClassNum !== undefined)
    queryParams.set(URL_KEYS.CHILDREN_LOWER_GRADE, String(params.lowClassNum));
  if (params.infantWithMBNum !== null && params.infantWithMBNum !== undefined)
    queryParams.set(URL_KEYS.INFANT_BED_MEALS, String(params.infantWithMBNum));
  if (params.infantWithMNum !== null && params.infantWithMNum !== undefined)
    queryParams.set(URL_KEYS.INFANT_MEAL, String(params.infantWithMNum));
  if (params.infantWithBNum !== null && params.infantWithBNum !== undefined)
    queryParams.set(URL_KEYS.INFANT_BED, String(params.infantWithBNum));
  if (params.infantWithoutMBNum !== null && params.infantWithoutMBNum !== undefined)
    queryParams.set(URL_KEYS.INFANT_NONE, String(params.infantWithoutMBNum));

  // Pagination parameters
  if (params.offset !== null && params.offset !== undefined)
    queryParams.set(URL_KEYS.HYOJI, String(params.offset));
  if (params.limit !== null && params.limit !== undefined)
    queryParams.set('limit', String(params.limit));

  // Filter parameters
  if (params.squeezeCondition?.length) {
    params.squeezeCondition.forEach((sq) => queryParams.append(URL_KEYS.SQUEEZES, sq));
  }
  if (params.roomCondition?.length) {
    params.roomCondition.forEach((rc) => queryParams.append('roomCondition', rc));
  }
  if (params.pointMin) queryParams.set('pointMin', params.pointMin);
  if (params.roomEquip) queryParams.set('roomEquip', params.roomEquip);
  if (params.roomType?.length) {
    params.roomType.forEach((rt) => queryParams.append('roomType', rt));
  }
  if (params.roomBedType?.length) {
    params.roomBedType.forEach((rbt) => queryParams.append('roomBedType', rbt));
  }
  if (params.minCharge !== null && params.minCharge !== undefined)
    queryParams.set(URL_KEYS.KIN, String(params.minCharge));
  if (params.maxCharge !== null && params.maxCharge !== undefined)
    queryParams.set(URL_KEYS.KIN2, String(params.maxCharge));

  // Sorting and selection
  if (params.sort) queryParams.set(URL_KEYS.SORT, params.sort);
  if (params.providerIds?.length) {
    params.providerIds.forEach((id) => queryParams.append(URL_KEYS.NUMBER, String(id)));
  }

  return queryParams.toString();
};
